/**
 * Content Blocker für externe Inhalte wie YouTube Videos
 * Zeigt Consent-Banner an, wenn Inhalte blockiert sind
 */
import { pmLog, pmError } from './Logger.js';

export class ContentBlocker {
    constructor(banner) {
        this.banner = banner;
        this.config = banner?.config || window.pixelmateConfig || {};
        this.design = banner?.design || this.config.design || {};
        this.csp = this.config.csp || {};
        this.activeServices = this.config.activeServices || {};

        // Service-spezifische Consent-Stati (nicht kategorie-basiert)
        this.serviceConsent = {
            youtube: false,
            vimeo: false,
            dailymotion: false,
            spotify: false,
            google_maps: false,
            soundcloud: false,
            open_street_map: false
        };

        // Geblockte Elemente pro Service tracken
        this.blockedElements = new Map();
        // Original-Elemente die bereits verarbeitet wurden (verhindert doppeltes Blockieren)
        this.blockedOriginalElements = new Set();
        // Service -> Set of containers
        this.blockedElementsByService = {
            youtube: new Set(),
            vimeo: new Set(),
            dailymotion: new Set(),
            spotify: new Set(),
            google_maps: new Set(),
            soundcloud: new Set(),
            open_street_map: new Set()
        };

        pmLog('ContentBlocker initialized with service-specific consent');
    }

    /**
     * Initialisierung - sucht und blockiert YouTube-Inhalte
     */
    init() {
        // SOFORT Mutation Observer starten (vor allem anderen)
        // Dies fängt neue iframes ab, bevor sie geladen werden
        this.observeNewContent();

        // Dann bestehende Inhalte scannen
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.scanAndBlockContent());
        } else {
            this.scanAndBlockContent();
        }
    }

    /**
     * Prüft ob YouTube blockiert werden soll
     */
    shouldBlockYouTube() {
        return this.shouldBlockService('youtube', 'allowYoutube');
    }

    /**
     * Prüft ob Vimeo blockiert werden soll
     */
    shouldBlockVimeo() {
        return this.shouldBlockService('vimeo', 'allowVimeo');
    }

    /**
     * Prüft ob Dailymotion blockiert werden soll
     */
    shouldBlockDailymotion() {
        return this.shouldBlockService('dailymotion', 'allowDailymotion');
    }

    /**
     * Prüft ob Spotify blockiert werden soll
     */
    shouldBlockSpotify() {
        return this.shouldBlockService('spotify', 'allowSpotify');
    }

    /**
     * Prüft ob Google Maps blockiert werden soll
     */
    shouldBlockGoogleMaps() {
        return this.shouldBlockService('google_maps', 'allowGoogleMaps');
    }

    /**
     * Prüft ob SoundCloud blockiert werden soll
     */
    shouldBlockSoundCloud() {
        return this.shouldBlockService('soundcloud', 'allowSoundCloud');
    }

    /**
     * Prüft ob OpenStreetMap blockiert werden soll
     */
    shouldBlockOpenStreetMap() {
        return this.shouldBlockService('open_street_map', 'allowOpenStreetMap');
    }

    /**
     * Generische Methode zur Prüfung ob ein Service blockiert werden soll.
     * Wird auch innerhalb des MutationObservers aufgerufen – muss daher stets
     * den AKTUELLEN Consent-Status widerspiegeln, nicht den Zustand bei init().
     */
    shouldBlockService(serviceName, cspWhitelistKey) {
        // Zuerst prüfen: Hat der User bereits Consent für diesen Service gegeben?
        if (this.serviceConsent[serviceName]) {
            pmLog(`${serviceName} not blocked: User has given service consent`);
            return false;
        }

        // Aktuellen Consent aus dem In-Page-State lesen.
        // pixelmateEarlyConsent wird von setConsent() aktualisiert sobald der User
        // zustimmt – so sehen auch asynchron laufende MutationObserver den neuen Zustand.
        // Hinweis: getCookie() wird hier NICHT als Fallback verwendet, da JSDOM im
        // Testumfeld Cookies zwischen Tests persistiert und so falsch-negative Ergebnisse
        // erzeugen würde.
        const earlyConsent = window.pixelmateEarlyConsent;

        if (earlyConsent !== undefined && earlyConsent !== null) {
            if (earlyConsent.type === 'accept') {
                pmLog(`${serviceName} not blocked: Accept-all consent present`);
                return false;
            }
            if (earlyConsent.categories && earlyConsent.categories.external === true) {
                pmLog(`${serviceName} not blocked: External category consent present`);
                return false;
            }
            if (earlyConsent.services && earlyConsent.services[serviceName] === true) {
                pmLog(`${serviceName} not blocked: Service-specific consent present`);
                return false;
            }
        }

        // Fall 1: CSP ist aktiv UND Service ist nicht in der Whitelist
        const cspActive = this.csp.active === true || this.csp.active === 'true';
        const serviceInCspWhitelist = this.csp[cspWhitelistKey] === true || this.csp[cspWhitelistKey] === 'true';

        if (cspActive && !serviceInCspWhitelist) {
            pmLog(`${serviceName} blocked: CSP active, ${serviceName} not in whitelist`);
            return true;
        }

        // Fall 2: Integration Manager hat Service aktiviert
        const serviceEnabled = this.activeServices[serviceName] && this.activeServices[serviceName].enabled;

        if (serviceEnabled) {
            pmLog(`${serviceName} blocked: Integration Manager active`);
            return true;
        }

        // Fall 3: Weder CSP noch Integration Manager aktiv → nicht blockieren
        pmLog(`${serviceName} not blocked: Neither CSP nor Integration Manager active`);
        return false;
    }

    /**
     * Scannt Seite nach blockierten Inhalten und blockiert diese
     */
    scanAndBlockContent() {
        const shouldBlockYT = this.shouldBlockYouTube();
        const shouldBlockVM = this.shouldBlockVimeo();
        const shouldBlockDM = this.shouldBlockDailymotion();
        const shouldBlockSP = this.shouldBlockSpotify();
        const shouldBlockGM = this.shouldBlockGoogleMaps();
        const shouldBlockSC = this.shouldBlockSoundCloud();
        const shouldBlockOSM = this.shouldBlockOpenStreetMap();

        if (!shouldBlockYT && !shouldBlockVM && !shouldBlockDM && !shouldBlockSP && !shouldBlockGM && !shouldBlockSC && !shouldBlockOSM) {
            pmLog('No content blocking required');
            return;
        }

        pmLog('Scanning for blocked content...');

        // 1. Finde Elementor Videos
        if (shouldBlockYT || shouldBlockVM || shouldBlockDM || shouldBlockSP) {
            this.scanElementorVideos(shouldBlockYT, shouldBlockVM, shouldBlockDM, shouldBlockSP);
        }

        // 2. Finde YouTube iframes
        if (shouldBlockYT) {
            const youtubeSelectors = [
                'iframe[src*="youtube.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="youtube-nocookie.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="youtu.be"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="youtube"]'
            ];

            const youtubeIframes = document.querySelectorAll(youtubeSelectors.join(','));
            pmLog(`Found ${youtubeIframes.length} YouTube iframe(s)`);

            youtubeIframes.forEach(iframe => {
                this.handleIframe(iframe, 'youtube');
            });
        }

        // 3. Finde Vimeo iframes
        if (shouldBlockVM) {
            const vimeoSelectors = [
                'iframe[src*="vimeo.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="player.vimeo.com"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="vimeo"]'
            ];

            const vimeoIframes = document.querySelectorAll(vimeoSelectors.join(','));
            pmLog(`Found ${vimeoIframes.length} Vimeo iframe(s)`);

            vimeoIframes.forEach(iframe => {
                this.handleIframe(iframe, 'vimeo');
            });
        }

        // 4. Finde Dailymotion iframes
        if (shouldBlockDM) {
            const dailymotionSelectors = [
                'iframe[src*="dailymotion.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="dai.ly"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="dailymotion"]'
            ];

            const dailymotionIframes = document.querySelectorAll(dailymotionSelectors.join(','));
            pmLog(`Found ${dailymotionIframes.length} Dailymotion iframe(s)`);

            dailymotionIframes.forEach(iframe => {
                this.handleIframe(iframe, 'dailymotion');
            });
        }

        // 5. Finde Spotify iframes
        if (shouldBlockSP) {
            const spotifySelectors = [
                'iframe[src*="spotify.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="open.spotify.com"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="spotify"]'
            ];

            const spotifyIframes = document.querySelectorAll(spotifySelectors.join(','));
            pmLog(`Found ${spotifyIframes.length} Spotify iframe(s)`);

            spotifyIframes.forEach(iframe => {
                this.handleIframe(iframe, 'spotify');
            });
        }

        // 5. Finde Google Maps iframes
        if (shouldBlockGM) {
            const mapsSelectors = [
                'iframe[src*="maps.google.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="maps.googleapis.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="google.com/maps"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="maps.google"]'
            ];

            const mapsIframes = document.querySelectorAll(mapsSelectors.join(','));
            pmLog(`Found ${mapsIframes.length} Google Maps iframe(s)`);

            mapsIframes.forEach(iframe => {
                this.handleIframe(iframe, 'google_maps');
            });
        }

        // 6. Finde SoundCloud iframes
        if (shouldBlockSC) {
            const soundcloudSelectors = [
                'iframe[src*="soundcloud.com"]:not([data-pixelmate-blocked])',
                'iframe[src*="w.soundcloud.com"]:not([data-pixelmate-blocked])',
                'iframe[data-pixelmate-blocked="true"][data-src*="soundcloud"]'
            ];

            const soundcloudIframes = document.querySelectorAll(soundcloudSelectors.join(','));
            pmLog(`Found ${soundcloudIframes.length} SoundCloud iframe(s)`);

            soundcloudIframes.forEach(iframe => {
                this.handleIframe(iframe, 'soundcloud');
            });
        }

        // 7. Finde OpenStreetMap/Leaflet Karten
        if (shouldBlockOSM) {
            this.scanAndBlockOpenStreetMap();
        }
    }

    /**
     * Scannt Seite nach OpenStreetMap/Leaflet-Karten
     */
    scanAndBlockOpenStreetMap() {
        // Leaflet-Container suchen (die "leaflet-container" Klasse ist Pflicht für Leaflet-Maps)
        const leafletMaps = document.querySelectorAll('.leaflet-container:not([data-pixelmate-blocked])');
        pmLog(`Found ${leafletMaps.length} OpenStreetMap/Leaflet map(s)`);

        leafletMaps.forEach(map => {
            // Prüfen ob die Karte wirklich von OSM stammt (nach OSM-Tile-URLs suchen)
            const isOpenStreetMap = this.isOpenStreetMapContainer(map);
            if (isOpenStreetMap) {
                this.blockOpenStreetMapContainer(map);
            }
        });
    }

    /**
     * Prüft ob ein Leaflet-Container OpenStreetMap-Karten enthält
     * (im Gegensatz zu anderen Kartendiensten, die auch Leaflet nutzen können)
     */
    isOpenStreetMapContainer(container) {
        // Tile-Bilder von den OSM-Tile-Servern (a/b/c.tile.openstreetmap.org -
        // "tile.openstreetmap.org" als Substring deckt alle drei Subdomains ab).
        const tileImages = container.querySelectorAll('img[src*="tile.openstreetmap.org"]');
        if (tileImages.length > 0) {
            return true;
        }

        // ODbL-Lizenzbedingungen schreiben einen Attribution-Hinweis vor -
        // praktisch jede echte OSM-Einbindung zeigt ihn über
        // leaflet-control-attribution an, auch bevor Tiles fertig geladen sind.
        // innerHTML statt textContent, da der Nachweis meist im href des
        // Copyright-Links steckt (z.B. "openstreetmap.org/copyright") - der
        // sichtbare Linktext variiert je nach Locale/Plugin ("OpenStreetMap
        // contributors", "OpenStreetMap Mitwirkende", ...) und enthält die
        // Domain oft gar nicht wörtlich.
        const attribution = container.querySelector('.leaflet-control-attribution');
        if (attribution && /openstreetmap\.org/i.test(attribution.innerHTML || '')) {
            return true;
        }

        // Maps Marker Pro nutzt standardmäßig OSM-Tiles
        if (container.id && (container.id.includes('mmp-map') || container.classList.contains('maps-marker-pro'))) {
            return true;
        }

        // Kein OSM-Hinweis gefunden - NICHT pauschal als OSM behandeln. Andere
        // Kartendienste (Mapbox, CARTO, ...) nutzen ebenfalls Leaflet als
        // Rendering-Bibliothek; würde jeder .leaflet-container als OSM gelten,
        // bekäme der Besucher bei diesen Diensten einen falschen Anbieter samt
        // falschem Datenschutzlink angezeigt.
        return false;
    }

    /**
     * Blockiert einen OpenStreetMap-Container und ersetzt ihn mit einem Consent-Banner
     */
    blockOpenStreetMapContainer(container) {
        // Prüfen ob bereits blockiert
        if (container.hasAttribute('data-pixelmate-blocked')) {
            return;
        }

        container.setAttribute('data-pixelmate-blocked', 'true');
        pmLog('Blocking OpenStreetMap container:', container);

        // Banner-Container erstellen
        const banner = this.createBlockedContentContainer('', 'open_street_map');

        // Container-Höhe/-Breite übernehmen (für Aspect Ratio). Wird VOR dem
        // Verstecken der Karte ausgelesen, solange sie noch im Layout-Fluss
        // steht - danach liefert getComputedStyle() sonst "0px". "0px" wird
        // zusätzlich explizit verworfen: Trifft der MutationObserver den
        // Container, bevor Leaflet ihm eine Höhe zugewiesen hat (Karte noch
        // nicht vollständig initialisiert), wäre computedStyle.height sonst
        // "0px" - ein truthy String, der den '400px'-Fallback fälschlich
        // überspringen und den Platzhalter unsichtbar machen würde.
        const computedStyle = window.getComputedStyle(container);
        const computedHeight = computedStyle.height !== '0px' ? computedStyle.height : '';
        const computedWidth = computedStyle.width !== '0px' ? computedStyle.width : '';
        const containerHeight = container.style.height || computedHeight || '400px';
        const containerWidth = container.style.width || computedWidth || '100%';

        // Karte verstecken
        container.style.display = 'none';

        // Banner vorher einfügen
        container.parentNode.insertBefore(banner, container);

        // Größe des Banners an die Karte anpassen. Anders als beim iframe-
        // basierten YouTube/Vimeo-Platzhalter (padding-bottom-Trick für
        // 16:9) wird hier eine feste Höhe übernommen, da Leaflet-Karten
        // beliebige Seitenverhältnisse haben können.
        const blockedContent = banner.querySelector('.pixelmate-blocked-content');
        if (blockedContent) {
            blockedContent.style.cssText = `
                position: relative !important;
                width: ${containerWidth} !important;
                max-width: 100% !important;
                height: ${containerHeight} !important;
                background: #000 !important;
                border-radius: 8px !important;
                overflow: hidden !important;
                container-type: inline-size !important;
            `;
        }

        // In Map speichern
        this.blockedElements.set(banner, {
            originalElement: container,
            src: '',
            isOpenStreetMapContainer: true,
            service: 'open_street_map'
        });

        // Auch im Service-spezifischen Set speichern
        this.blockedElementsByService['open_street_map'].add(banner);
    }

    /**
     * Behandelt ein gefundenes iframe
     */
    handleIframe(iframe, service) {
        // Prüfen ob bereits von PHP blockiert
        if (iframe.hasAttribute('data-pixelmate-blocked')) {
            pmLog(`Found server-side blocked ${service} iframe`);
            // Bereits blockiert, nur Banner hinzufügen
            this.blockElement(iframe, true, service);
        } else if (!iframe.hasAttribute('data-consent')) {
            // Noch nicht blockiert, jetzt blockieren
            pmLog(`Blocking client-side ${service} iframe`);
            this.blockElement(iframe, false, service);
        }
    }

    /**
     * Scannt speziell nach Elementor Videos
     */
    scanElementorVideos(shouldBlockYT, shouldBlockVM, shouldBlockDM, shouldBlockSP) {
        // Elementor speichert Video-Infos in data-settings
        const elementorWidgets = document.querySelectorAll('.elementor-widget-video[data-settings]');
        pmLog(`Found ${elementorWidgets.length} Elementor video widget(s)`);

        elementorWidgets.forEach(widget => {
            try {
                const settings = widget.getAttribute('data-settings');
                if (!settings) return;

                // Parse JSON
                const settingsObj = JSON.parse(settings);

                // Serverseitig ausgelagerte echte URL hat Vorrang (siehe
                // YouTubeBlocker::block_elementor_content()): dort wird die
                // URL aus data-settings entfernt, damit Elementors eigenes
                // Frontend-Script sie nicht mehr lesen und ungefragt einen
                // Player initialisieren kann - settingsObj.*_url ist bei
                // server-blockierten Widgets deshalb leer.
                const realSrc = widget.getAttribute('data-pixelmate-video-src') || '';
                const effectiveUrl = realSrc || settingsObj.youtube_url || settingsObj.vimeo_url ||
                    settingsObj.dailymotion_url || settingsObj.spotify_url || settingsObj.video_url || '';

                // Prüfe ob YouTube-URL vorhanden
                const hasYouTube = effectiveUrl.includes('youtube') || effectiveUrl.includes('youtu.be') ||
                                   settingsObj.video_type === 'youtube';

                // Prüfe ob Vimeo-URL vorhanden
                const hasVimeo = effectiveUrl.includes('vimeo') || settingsObj.video_type === 'vimeo';

                // Prüfe ob Dailymotion-URL vorhanden
                const hasDailymotion = effectiveUrl.includes('dailymotion') || effectiveUrl.includes('dai.ly') ||
                    settingsObj.video_type === 'dailymotion';

                // Prüfe ob Spotify-URL vorhanden
                const hasSpotify = effectiveUrl.includes('spotify') || settingsObj.video_type === 'spotify';

                if (shouldBlockYT && hasYouTube) {
                    pmLog('Found Elementor YouTube video widget');
                    this.blockElementorWidget(widget, settingsObj, 'youtube');
                } else if (shouldBlockVM && hasVimeo) {
                    pmLog('Found Elementor Vimeo video widget');
                    this.blockElementorWidget(widget, settingsObj, 'vimeo');
                } else if (shouldBlockDM && hasDailymotion) {
                    pmLog('Found Elementor Dailymotion video widget');
                    this.blockElementorWidget(widget, settingsObj, 'dailymotion');
                } else if (shouldBlockSP && hasSpotify) {
                    pmLog('Found Elementor Spotify widget');
                    this.blockElementorWidget(widget, settingsObj, 'spotify');
                }
            } catch (e) {
                // JSON parse error - ignorieren
            }
        });

        // Auch nach anderen Page Builder Videos suchen
        if (shouldBlockYT) {
            const youtubeContainers = document.querySelectorAll('[class*="video"][data-settings*="youtube"], [class*="video"][data-settings*="youtu.be"]');
            youtubeContainers.forEach(container => {
                if (!container.classList.contains('elementor-widget-video')) {
                    pmLog('Found other page builder YouTube video');
                    this.blockElementorWidget(container, {}, 'youtube');
                }
            });
        }

        if (shouldBlockVM) {
            const vimeoContainers = document.querySelectorAll('[class*="video"][data-settings*="vimeo"]');
            vimeoContainers.forEach(container => {
                if (!container.classList.contains('elementor-widget-video')) {
                    pmLog('Found other page builder Vimeo video');
                    this.blockElementorWidget(container, {}, 'vimeo');
                }
            });
        }

        if (shouldBlockDM) {
            const dailymotionContainers = document.querySelectorAll('[class*="video"][data-settings*="dailymotion"], [class*="video"][data-settings*="dai.ly"]');
            dailymotionContainers.forEach(container => {
                if (!container.classList.contains('elementor-widget-video')) {
                    pmLog('Found other page builder Dailymotion video');
                    this.blockElementorWidget(container, {}, 'dailymotion');
                }
            });
        }

        if (shouldBlockSP) {
            const spotifyContainers = document.querySelectorAll('[class*="audio"][data-settings*="spotify"], [class*="music"][data-settings*="spotify"]');
            spotifyContainers.forEach(container => {
                if (!container.classList.contains('elementor-widget-video')) {
                    pmLog('Found other page builder Spotify widget');
                    this.blockElementorWidget(container, {}, 'spotify');
                }
            });
        }
    }

    /**
     * Blockiert ein Elementor-Widget komplett
     */
    blockElementorWidget(widget, settings, service = 'youtube') {
        // Prüfen ob bereits blockiert
        if (widget.hasAttribute('data-pixelmate-widget-blocked')) {
            return;
        }

        pmLog(`Blocking Elementor ${service} widget:`, widget);

        // Widget als blockiert markieren
        widget.setAttribute('data-pixelmate-widget-blocked', 'true');

        // Original-Inhalt verstecken
        const widgetWrapper = widget.querySelector('.elementor-widget-container');
        if (widgetWrapper) {
            widgetWrapper.style.display = 'none';
        }

        // Container für Banner erstellen und einfügen. Die serverseitig
        // ausgelagerte echte URL (data-pixelmate-video-src, siehe
        // YouTubeBlocker::block_elementor_content()) hat Vorrang, da
        // settings.*_url bei server-blockierten Widgets bereits geleert ist.
        const videoSrc = widget.getAttribute('data-pixelmate-video-src') ||
            settings.youtube_url || settings.vimeo_url || settings.dailymotion_url || settings.video_url || '';
        const container = this.createBlockedContentContainer(videoSrc, service);

        // Banner einfügen (nach dem Widget-Wrapper, damit es sichtbar ist)
        if (widgetWrapper) {
            widgetWrapper.parentNode.insertBefore(container, widgetWrapper);
        } else {
            widget.appendChild(container);
        }

        // In Map speichern
        this.blockedElements.set(container, {
            originalElement: widgetWrapper || widget,
            src: videoSrc,
            isWidget: true,
            service: service
        });

        // Auch im Service-spezifischen Set speichern
        this.blockedElementsByService[service].add(container);
    }

    /**
     * Blockiert ein einzelnes Element und ersetzt es durch Banner
     * @param {HTMLElement} element - Das zu blockierende iframe
     * @param {boolean} alreadyBlocked - Ob das Element bereits server-seitig blockiert wurde
     * @param {string} service - Der Service-Name (youtube, vimeo)
     */
    blockElement(element, alreadyBlocked = false, service = 'youtube') {
        // Prüfen ob bereits blockiert
        if (this.blockedElements.has(element) || this.blockedOriginalElements.has(element)) {
            return;
        }
        this.blockedOriginalElements.add(element);

        pmLog(`Blocking ${service} element:`, element);

        // Original src speichern
        const originalSrc = element.src || element.dataset.src || '';

        if (!alreadyBlocked) {
            // Client-seitig blockieren: src entfernen und in data-src speichern
            if (element.src) {
                element.dataset.src = element.src;
                element.removeAttribute('src');
            }
            element.dataset.consent = 'external';
            element.dataset.pixelmateBlocked = 'true';

            // Zusätzliche Sicherheitsattribute
            element.setAttribute('loading', 'lazy');
            element.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
        }

        // Finde den besten Parent-Container für das Banner
        // Prüfe ob das iframe in einem bekannten Video-Wrapper ist
        const videoWrapper = this.findVideoWrapper(element);

        // Container erstellen
        const container = this.createBlockedContentContainer(originalSrc, service);

        if (videoWrapper && videoWrapper !== element.parentNode) {
            // Verwende den Video-Wrapper statt nur das iframe-Parent
            pmLog('Using video wrapper for banner:', videoWrapper);

            // Alle Kinder des Wrappers verstecken
            Array.from(videoWrapper.children).forEach(child => {
                if (child !== container) {
                    child.style.display = 'none';
                }
            });

            // Banner als erstes Kind einfügen
            videoWrapper.insertBefore(container, videoWrapper.firstChild);

            // In Map speichern
            this.blockedElements.set(container, {
                originalElement: videoWrapper,
                originalChildren: Array.from(videoWrapper.children).filter(c => c !== container),
                src: originalSrc,
                isWrapper: true,
                service: service
            });

            // Auch im Service-spezifischen Set speichern
            this.blockedElementsByService[service].add(container);
        } else {
            // Standard-Verhalten: vor dem Element einfügen
            element.parentNode.insertBefore(container, element);
            element.style.display = 'none';

            // In Map speichern
            this.blockedElements.set(container, {
                originalElement: element,
                src: originalSrc,
                service: service
            });

            // Auch im Service-spezifischen Set speichern
            this.blockedElementsByService[service].add(container);
        }
    }

    /**
     * Findet den besten Video-Wrapper für ein iframe
     */
    findVideoWrapper(iframe) {
        let current = iframe.parentNode;
        let depth = 0;
        const maxDepth = 5; // Nicht zu weit nach oben suchen

        while (current && depth < maxDepth) {
            // Prüfe ob es ein bekannter Video-Container ist
            const classList = current.classList ? Array.from(current.classList) : [];
            const className = current.className || '';

            // Bekannte Video-Wrapper-Klassen
            if (classList.some(cls =>
                cls.includes('video') ||
                cls.includes('player') ||
                cls.includes('embed') ||
                cls.includes('responsive')
            )) {
                return current;
            }

            // Elementor-spezifisch
            if (classList.includes('elementor-widget-container')) {
                return current;
            }

            current = current.parentNode;
            depth++;
        }

        // Kein spezieller Wrapper gefunden
        return iframe.parentNode;
    }

    /**
     * Erstellt den Container mit Consent-Banner
     */
    createBlockedContentContainer(videoSrc, service = 'youtube') {
        const container = document.createElement('div');
        container.className = 'pixelmate-blocked-content';
        container.dataset.service = service;

        // Größe vom Parent übernehmen oder Standard
        container.style.cssText = `
            position: relative !important;
            width: 100% !important;
            padding-bottom: 56.25% !important; /* 16:9 Aspect Ratio */
            background: #000 !important;
            border-radius: 8px !important;
            overflow: hidden !important;
            container-type: inline-size !important;
        `;

        // Thumbnail-Ebene: wird per lokalem Proxy-Cache befüllt (siehe loadThumbnail),
        // damit ein echtes Vorschaubild angezeigt werden kann, OHNE dass der Browser
        // des Besuchers selbst eine Verbindung zu YouTube/Vimeo aufbaut.
        if (service === 'youtube' || service === 'vimeo') {
            const thumbnail = document.createElement('div');
            thumbnail.className = 'pixelmate-content-banner-thumbnail';
            thumbnail.style.cssText = `
                position: absolute !important;
                inset: 0 !important;
                background-size: cover !important;
                background-position: center !important;
                background-repeat: no-repeat !important;
                z-index: 1 !important;
            `;
            container.appendChild(thumbnail);

            const overlay = document.createElement('div');
            overlay.className = 'pixelmate-content-banner-overlay';
            overlay.style.cssText = `
                position: absolute !important;
                inset: 0 !important;
                background: rgba(0, 0, 0, 0.55) !important;
                z-index: 2 !important;
            `;
            container.appendChild(overlay);

            this.loadThumbnail(thumbnail, videoSrc, service);
        }

        // Banner erstellen
        const banner = this.createConsentBanner(videoSrc, service);
        container.appendChild(banner);

        return container;
    }

    /**
     * Holt ein lokal gecachtes Vorschaubild über den eigenen Server (admin-ajax.php)
     * und setzt es als Hintergrund. Der Client kontaktiert dabei ausschließlich die
     * eigene Domain - YouTube/Vimeo werden nur einmalig server-seitig kontaktiert
     * (siehe Pixelmate_ThumbnailProxy), die Besucher-IP wird nie an sie übertragen.
     */
    loadThumbnail(thumbnailEl, videoSrc, service) {
        if (!videoSrc) return;

        const ajaxUrl = this.config.ajax?.url;
        const nonce = this.config.ajax?.nonce;
        if (!ajaxUrl || !nonce) return;

        const body = new URLSearchParams();
        body.set('action', 'pixelmate_get_thumbnail');
        body.set('nonce', nonce);
        body.set('service', service);
        body.set('src', videoSrc);

        fetch(ajaxUrl, { method: 'POST', body, credentials: 'same-origin' })
            .then(response => response.json())
            .then(data => {
                if (data && data.success && data.data && data.data.url) {
                    thumbnailEl.style.backgroundImage = `url("${data.data.url}")`;
                }
            })
            .catch(() => {
                // Kein Vorschaubild verfügbar - Banner bleibt ohne Hintergrundbild
            });
    }

    /**
     * Erstellt das Consent-Banner im Style des Cookie-Banners
     */
    createConsentBanner(videoSrc, service = 'youtube') {
        const banner = document.createElement('div');
        banner.className = 'pixelmate-content-banner';

        // Der Container-Hintergrund ist immer schwarz (Platzhalter für den
        // eingebetteten Inhalt), daher muss die Schrift unabhängig vom
        // Banner-Design immer weiß sein, um lesbar zu bleiben.
        const textColor = '#ffffff';
        const buttonBg = this.design.buttons?.accept?.background || '#0073aa';
        const buttonText = this.design.buttons?.accept?.text || '#ffffff';

        // WICHTIG: Layout, Größe, Zentrierung und der dunkle/geblurrte
        // Hintergrund werden komplett INLINE gesetzt (nicht über die
        // pixelmate.css-Klasse). Themes laden ihr eigenes CSS oft nach dem
        // Plugin-Stylesheet bzw. mit höherer Priorität/Cache-Eigenheiten, und
        // ein fehlendes/überschriebenes Stylesheet darf niemals dazu führen,
        // dass die Karte unformatiert und nicht zentriert im DOM-Fluss landet.
        // Inline-Styles mit !important gewinnen gegen jedes Theme-CSS, auch
        // gegen dortige !important-Regeln (Autor-Herkunft + Inline-Spezifität).
        // .pixelmate-blocked-content setzt bereits inline container-type,
        // daher funktionieren die cqw-Einheiten unten auch ohne Stylesheet.
        banner.style.cssText = `
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            width: 350px !important;
            max-width: calc(100% - 24px) !important;
            max-height: calc(100% - 24px) !important;
            margin: 0 !important;
            padding: 28px 22px !important;
            border-radius: 12px !important;
            background: rgba(12, 12, 16, 0.36) !important;
            backdrop-filter: blur(20px) !important;
            -webkit-backdrop-filter: blur(20px) !important;
            box-shadow: rgba(0, 0, 0, 0.35) 0px 8px 32px !important;
            border: 1px solid rgba(255, 255, 255, .15) !important;
            text-align: center !important;
            box-sizing: border-box !important;
            overflow-y: auto !important;
            z-index: 10 !important;
        `;

        // Fallback für Browser ohne backdrop-filter-Unterstützung: Karte ohne
        // Weichzeichner-Effekt trotzdem stark genug abdunkeln, damit der
        // WCAG-AA-Kontrast der weißen Schrift erhalten bleibt.
        const supportsBackdropFilter = typeof CSS !== 'undefined' && CSS.supports &&
            (CSS.supports('backdrop-filter', 'blur(1px)') || CSS.supports('-webkit-backdrop-filter', 'blur(1px)'));
        if (!supportsBackdropFilter) {
            banner.style.setProperty('background', 'rgba(10, 10, 14, 0.94)', 'important');
        }

        // Texte abhängig vom Service (keine Logos - siehe Vorgabe: "Logos weglassen")
        const serviceConfig = this.getServiceConfig(service);

        // Eindeutige ID für die Titel-Zuordnung (aria-labelledby) - mehrere
        // blockierte Einbettungen können gleichzeitig auf der Seite stehen.
        const uid = `pixelmate-cb-${Math.random().toString(36).slice(2, 9)}`;

        // Titel - bewusst KEINE Überschrift (h1-h6), da diese Karte kein
        // eigenständiger Dokumentabschnitt ist, sondern ein Platzhalter für
        // einen einzelnen eingebetteten Inhalt; ein div verhindert, dass sie
        // in der Screenreader-Überschriftennavigation der Seite auftaucht
        // und dort verwirrt. Semantik/Fokusziel liefert stattdessen
        // role="group" + aria-labelledby auf der Karte (siehe unten).
        const title = document.createElement('div');
        title.className = 'pixelmate-content-banner-title';
        title.id = `${uid}-title`;
        title.textContent = serviceConfig.title;
        title.style.cssText = `
            margin: 0 0 clamp(3px, 1.5cqw, 6px) 0 !important;
            font-size: clamp(12px, 4cqw, 15px) !important;
            font-weight: 600 !important;
            line-height: 1.25 !important;
            color: ${textColor} !important;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8) !important;
        `;

        // Karte als benannte Gruppe ausweisen, da der Titel jetzt kein
        // Heading-Element mehr ist - Screenreader-Nutzer bekommen beim
        // Fokussieren/Betreten trotzdem den Titel als zugängen Namen.
        banner.setAttribute('role', 'group');
        banner.setAttribute('aria-labelledby', title.id);

        // Beschreibung
        const description = document.createElement('p');
        description.className = 'pixelmate-content-banner-text';
        description.id = `${uid}-desc`;
        const bannerText = this.activeServices[service]?.bannerText || serviceConfig.defaultText;
        description.textContent = bannerText;
        description.style.cssText = `
            margin: 0 0 clamp(6px, 2.5cqw, 12px) 0 !important;
            font-size: clamp(11px, 3cqw, 13px) !important;
            line-height: 1.4 !important;
            max-width: 100% !important;
            color: ${textColor} !important;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8) !important;
        `;

        // Button zum Freischalten: rundes Play-Icon statt Text-Button (siehe
        // Vorgabe "Logos weglassen" / kompakteres UI). Da der Button nur ein
        // Icon zeigt, trägt er zwingend ein aria-label mit dem eigentlichen
        // Handlungstext sowie aria-describedby auf die Beschreibung, damit
        // Screenreader-Nutzer die gleiche Information wie sehende Nutzer haben.
        const unlockButtonLabel = this.config.settings?.texts?.unlockContentButton || 'Inhalt freischalten';
        const unlockButton = document.createElement('button');
        unlockButton.type = 'button';
        unlockButton.className = 'pixelmate-unlock-content-btn';
        unlockButton.setAttribute('aria-label', unlockButtonLabel);
        unlockButton.setAttribute('aria-describedby', description.id);
        unlockButton.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                <path d="M8 5.5v13l11-6.5-11-6.5z" fill="#ffffff"/>
            </svg>
        `;
        const unlockButtonBaseBg = 'rgba(255, 255, 255, .1)';
        const unlockButtonHoverBg = 'rgba(255, 255, 255, .2)';
        unlockButton.style.cssText = `
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 52px !important;
            height: 52px !important;
            border: none !important;
            border-radius: 50% !important;
            cursor: pointer !important;
            transition: background-color 0.2s !important;
            flex-shrink: 0 !important;
            background: ${unlockButtonBaseBg} !important;
            color: ${buttonText} !important;
            padding: 0 !important;
        `;

        // Checkbox: Freigabe optional für die gesamte Website (persistentes Cookie)
        // statt nur für diese eine Einbettung im aktuellen Seitenaufruf speichern.
        const sitewideLabel = document.createElement('label');
        sitewideLabel.className = 'pixelmate-content-banner-consent-option';
        sitewideLabel.style.cssText = `
            display: flex !important;
            align-items: flex-start !important;
            justify-content: center !important;
            gap: clamp(4px, 1.5cqw, 6px) !important;
            margin-top: clamp(6px, 2.5cqw, 10px) !important;
            max-width: 100% !important;
            cursor: pointer !important;
            text-align: left !important;
            color: ${textColor} !important;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8) !important;
        `;

        const sitewideCheckbox = document.createElement('input');
        sitewideCheckbox.type = 'checkbox';
        sitewideCheckbox.className = 'pixelmate-content-banner-consent-checkbox';
        sitewideCheckbox.checked = true;
        sitewideCheckbox.style.cssText = `
            flex-shrink: 0 !important;
            margin: 2px 0 0 0 !important;
            width: 13px !important;
            height: 13px !important;
            cursor: pointer !important;
            accent-color: ${buttonBg} !important;
        `;

        const sitewideText = document.createElement('span');
        sitewideText.className = 'pixelmate-content-banner-consent-text';
        sitewideText.textContent = this.getSitewideConsentLabel(serviceConfig.name);
        sitewideText.style.cssText = `
            font-size: clamp(10px, 2.6cqw, 11px) !important;
            line-height: 1.35 !important;
        `;

        sitewideLabel.appendChild(sitewideCheckbox);
        sitewideLabel.appendChild(sitewideText);

        unlockButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.unlockContent(banner.closest('.pixelmate-blocked-content'), sitewideCheckbox.checked);
        });

        unlockButton.addEventListener('mouseenter', () => {
            unlockButton.style.setProperty('background', unlockButtonHoverBg, 'important');
        });

        unlockButton.addEventListener('mouseleave', () => {
            unlockButton.style.setProperty('background', unlockButtonBaseBg, 'important');
        });

        // Gleiches Hover-Feedback auch bei Tastaturfokus (Tab-Navigation),
        // damit Nutzer ohne Maus sehen, welches Element aktuell aktiv ist.
        unlockButton.addEventListener('focus', () => {
            unlockButton.style.setProperty('background', unlockButtonHoverBg, 'important');
        });

        unlockButton.addEventListener('blur', () => {
            unlockButton.style.setProperty('background', unlockButtonBaseBg, 'important');
        });

        // Link zu Datenschutzeinstellungen
        const settingsLink = document.createElement('a');
        settingsLink.className = 'pixelmate-content-banner-link';
        settingsLink.textContent = this.config.settings?.texts?.contentSettingsLink || 'Datenschutzeinstellungen';
        settingsLink.href = '#';
        settingsLink.style.cssText = `
            display: block !important;
            margin-top: clamp(6px, 2.5cqw, 10px) !important;
            font-size: clamp(10px, 2.5cqw, 11px) !important;
            text-decoration: underline !important;
            color: ${textColor} !important;
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8) !important;
        `;
        settingsLink.setAttribute('role', 'button');

        settingsLink.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.banner && this.banner.settingsModal) {
                this.banner.settingsModal.open();
            }
        });

        // Zusammenfügen
        banner.appendChild(title);
        banner.appendChild(description);
        banner.appendChild(unlockButton);
        banner.appendChild(sitewideLabel);
        banner.appendChild(settingsLink);

        return banner;
    }

    /**
     * Baut den Checkbox-Label-Text "<Dienst> für die gesamte Website
     * freigeben (<Tage> Tage)." aus dem übersetzten Template und der
     * tatsächlich konfigurierten Consent-Dauer zusammen.
     */
    getSitewideConsentLabel(serviceDisplayName) {
        const template = this.config.settings?.texts?.allowServiceSitewideLabel
            || '%1$s für die gesamte Website freigeben (%2$d Tage).';
        const days = this.config.cookies?.acceptDuration || 365;

        return template.replace('%1$s', serviceDisplayName).replace('%2$d', days);
    }

    /**
     * Entsperrt Content und aktiviert nur den spezifischen Service
     * @param {HTMLElement} container - Der Placeholder-Container
     * @param {boolean} persistSitewide - Ob die Freigabe per Cookie für die
     *   gesamte Website gespeichert werden soll (Checkbox im Banner) oder
     *   nur für den aktuellen Seitenaufruf gilt.
     */
    unlockContent(container, persistSitewide = true) {
        // Ermittle welcher Service entsperrt werden soll
        const service = container.dataset.service;
        if (!service) {
            pmError('No service specified for container');
            return;
        }

        pmLog(`Unlocking ${service} content... (persistSitewide: ${persistSitewide})`);

        // Consent für diesen spezifischen Service setzen
        this.serviceConsent[service] = true;

        // ScriptBlocker mit Service-spezifischem Consent aktualisieren
        if (this.banner && this.banner.scriptBlocker) {
            const serviceStates = { [service]: true };
            this.banner.scriptBlocker.updateConsent({}, serviceStates);
        }

        // Prüfen ob CSP aktiv ist
        const cspActive = this.csp.active === true || this.csp.active === 'true';

        const reloadOnConsentChange = this.csp.reloadOnConsentChange === true || this.csp.reloadOnConsentChange === 'true';

        // Die CSP wird als <meta>-Tag beim initialen Seitenaufruf gesendet und
        // blockiert frame-src für nicht freigegebene Domains browserseitig -
        // ein rein JS-seitiges Setzen von iframe.src (unblockServiceContent)
        // schlägt dann unabhängig von der Checkbox fehl (der Browser verwirft
        // den Request wegen der CSP-Direktive, das iframe bleibt leer). Bei
        // aktiver CSP ist ein Reload daher immer nötig, damit die Whitelist
        // greift - unabhängig davon, ob die Freigabe dauerhaft (Checkbox an)
        // oder nur für die aktuelle Sitzung gelten soll (Checkbox aus).
        // reloadOnConsentChange darf hier NICHT mit hineinspielen: die Option
        // steuert nur, ob nach einer Consent-Änderung generell neu geladen
        // wird. Bei aktiver CSP ist der Reload dagegen technisch zwingend -
        // ohne ihn setzt zwar unblockServiceContent() den iframe-src, der
        // Browser verwirft den Request aber weiterhin wegen der bereits
        // ausgelieferten frame-src-Direktive, und für den Besucher "passiert
        // nichts". Genau dieser Fall trat nach "Alle ablehnen" + Klick auf
        // "Inhalt freischalten" auf, solange die Option deaktiviert war
        // (Standard).
        const needsReloadForCSP = cspActive;

        if (persistSitewide || needsReloadForCSP) {
            // Cookie mit Service-spezifischem Consent aktualisieren. Ohne
            // angehakte Checkbox nur als Sitzungs-Cookie (kein "expires"),
            // damit der Reload die CSP-Whitelist trifft, ohne die Freigabe
            // über die aktuelle Browser-Sitzung hinaus zu speichern.
            this.updateConsentCookie(service, persistSitewide);
        }

        if (needsReloadForCSP) {
            // CSP aktiv + Reload-Option aktiviert → Seite neu laden damit CSP-Whitelist greift
            pmLog(`CSP active and reloadOnConsentChange enabled, reloading to allow ${service}...`);
            this.banner.trackingDataPreserver.saveTrackingDataBeforeReload();
            window.location.reload();
        } else {
            // Kein Reload: Service direkt über JS-Ebene entsperren
            pmLog(`Unblocking ${service} without reload (CSP: ${cspActive}, reload: ${reloadOnConsentChange})`);
            this.unblockServiceContent(service);
        }
    }

    /**
     * Entsperrt alle blockierten Inhalte eines spezifischen Services
     */
    unblockServiceContent(service) {
        // Consent-State sofort setzen, damit shouldBlockService() und der
        // MutationObserver in observeNewContent() keine neu hinzugefügten Iframes
        // dieses Services mehr blockieren (z.B. nach Lazy-Load-Klick).
        this.serviceConsent[service] = true;

        const serviceContainers = this.blockedElementsByService[service];
        if (!serviceContainers || serviceContainers.size === 0) {
            pmLog(`No blocked ${service} content found`);
            return;
        }

        pmLog(`Unblocking ${serviceContainers.size} ${service} element(s)...`);

        serviceContainers.forEach(container => {
            const data = this.blockedElements.get(container);
            if (!data) return;

            const { originalElement, originalChildren, src, isWrapper, isWidget, isOpenStreetMapContainer } = data;
            let keepContainerInDom = false;

            if (isOpenStreetMapContainer) {
                // OpenStreetMap-Container: Original-Karte wieder anzeigen
                originalElement.style.display = '';
                pmLog('  → Restored OpenStreetMap container');
            } else if (isWrapper) {
                // Wrapper-Typ: Alle Kinder wieder anzeigen
                if (originalChildren) {
                    originalChildren.forEach(child => {
                        child.style.display = '';
                    });
                }
                // Iframes innerhalb des Wrappers src wiederherstellen (falls PHP sie blockiert hat)
                const wrappedIframes = originalElement.querySelectorAll ? originalElement.querySelectorAll('iframe[data-src]') : [];
                wrappedIframes.forEach(iframe => {
                    if (iframe.dataset.src) {
                        const iframeService = iframe.dataset.service || service;
                        iframe.src = this.getActivationUrl(iframe.dataset.src, iframeService);
                        iframe.removeAttribute('data-src');
                        iframe.removeAttribute('sandbox');
                        pmLog('  → Restored wrapped iframe src:', iframe.src);
                    }
                });
            } else if (isWidget) {
                // Widget-Blocked-Flag entfernen
                const widget = originalElement.closest('[data-pixelmate-widget-blocked]');
                if (widget) {
                    widget.removeAttribute('data-pixelmate-widget-blocked');
                }

                // Serverseitig ausgelagerte echte URL (siehe
                // YouTubeBlocker::block_elementor_content()): Elementors
                // eigenes Frontend-Script hat seinen Player beim Seitenaufruf
                // ohne echte URL initialisiert und rendert deshalb nichts
                // Funktionsfähiges. Ein reines Wiedereinblenden des Original-
                // Wrappers würde also nur eine leere/kaputte Fläche zeigen -
                // Pixelmate baut den eingebetteten Inhalt stattdessen selbst
                // als iframe auf (wie bei regulären <iframe>-Einbettungen).
                const realSrc = widget ? widget.getAttribute('data-pixelmate-video-src') : null;

                if (realSrc) {
                    widget.removeAttribute('data-pixelmate-video-src');
                    this.replaceContainerWithIframe(container, realSrc, service);
                    keepContainerInDom = true;
                } else {
                    // Kein ausgelagerter Real-Src (z.B. server-blockierter
                    // Iframe innerhalb eines sonst unangetasteten Widgets) →
                    // bisheriges Verhalten: Original-Wrapper wieder anzeigen.
                    originalElement.style.display = '';

                    const widgetIframes = originalElement.querySelectorAll ? originalElement.querySelectorAll('iframe[data-src]') : [];
                    widgetIframes.forEach(iframe => {
                        if (iframe.dataset.src) {
                            const iframeService = iframe.dataset.service || service;
                            iframe.src = this.getActivationUrl(iframe.dataset.src, iframeService);
                            iframe.removeAttribute('data-src');
                            iframe.removeAttribute('sandbox');
                            pmLog('  → Restored widget iframe src:', iframe.src);
                        }
                    });
                }
            } else {
                // Standard iframe: wieder anzeigen
                originalElement.style.display = '';

                // Src setzen falls noch nicht gesetzt
                const activationSrc = this.getActivationUrl(src, service);
                if (!originalElement.src && activationSrc) {
                    originalElement.src = activationSrc;
                    originalElement.removeAttribute('sandbox');
                } else if (originalElement.getAttribute('sandbox')) {
                    originalElement.removeAttribute('sandbox');
                }
            }

            // Container entfernen (außer er wurde gerade selbst mit einem
            // echten iframe befüllt, siehe replaceContainerWithIframe())
            if (!keepContainerInDom) {
                container.remove();
            }

            // Aus Map entfernen
            this.blockedElements.delete(container);
        });

        // Service-Set leeren
        serviceContainers.clear();

        // Blockierte Scripts werden vom ScriptBlocker via updateConsent() wiederhergestellt
        // (type="text/plain" + data-consent-service → activateScript). Kein eigener Code nötig.

        pmLog(`All ${service} content unblocked`);
    }

    /**
     * Ersetzt den Inhalt eines Pixelmate-Platzhalter-Containers (Consent-Banner)
     * durch einen echten iframe. Wird für Page-Builder-Video-Widgets (z.B.
     * Elementors natives Video-Widget) genutzt, deren eigener Player beim
     * Seitenaufruf ohne echte URL initialisiert wurde (siehe
     * YouTubeBlocker::block_elementor_content()) und deshalb funktionslos
     * bleibt - Pixelmate rendert den eingebetteten Inhalt in diesem Fall
     * selbst, statt sich auf das Page-Builder-eigene Script zu verlassen.
     * Der Container hat durch createBlockedContentContainer() bereits die
     * passende 16:9-Aspect-Ratio-Umhüllung (position:relative + padding-bottom),
     * daher genügt ein absolut positionierter iframe als einziges Kind.
     */
    replaceContainerWithIframe(container, videoSrc, service) {
        container.innerHTML = '';

        const iframe = document.createElement('iframe');
        iframe.src = this.getActivationUrl(videoSrc, service);
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute(
            'allow',
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        );
        iframe.style.cssText = `
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            height: 100% !important;
            border: 0 !important;
        `;

        container.appendChild(iframe);
    }

    /**
     * Entsperrt alle blockierten Inhalte aller Services (z.B. nach "Alle akzeptieren")
     */
    unblockAllConsentedServices() {
        Object.keys(this.blockedElementsByService).forEach(service => {
            if (this.blockedElementsByService[service].size > 0) {
                pmLog(`Unblocking all ${service} content after accept all`);
                this.unblockServiceContent(service);
            }
        });
    }

    /**
     * Aktualisiert CSP-Whitelist dynamisch (für bereits geladene Seite)
     */
    updateCSPWhitelist() {
        // CSP kann nicht nachträglich geändert werden, da es im <meta> tag gesetzt wird
        // Aber wir setzen ein Flag, dass bei nächstem Seitenaufruf CSP YouTube erlaubt
        pmLog('YouTube will be allowed in CSP on next page load');
    }

    /**
     * Aktualisiert das Consent-Cookie mit service-spezifischem Consent
     * @param {string} service
     * @param {boolean} persistent - true: Cookie mit Ablaufdatum (dauerhaft).
     *   false: Sitzungs-Cookie ohne "expires" (wird beim Schließen des
     *   Browsers gelöscht) - genutzt wenn die Checkbox "für die gesamte
     *   Website freigeben" abgewählt wurde, ein Reload für die CSP-Whitelist
     *   aber trotzdem nötig ist (siehe unlockContent()).
     */
    updateConsentCookie(service, persistent = true) {
        const cookieName = this.config.cookies?.cookieName || 'pixelmate_consent';
        const existingConsent = this.getCookie(cookieName);

        let consentData = existingConsent || {
            type: 'custom',
            categories: {
                necessary: true,
                analytics: false,
                marketing: false,
                external: false
            },
            services: {}
        };

        // Sicherstellen dass services-Objekt existiert
        if (!consentData.services) {
            consentData.services = {};
        }

        // Service-spezifischen Consent aktivieren
        consentData.services[service] = true;
        consentData.timestamp = Date.now();

        // Cookie speichern
        this.setCookie(cookieName, consentData, persistent);

        pmLog(`Consent cookie updated for service ${service} (persistent: ${persistent}):`, consentData);
    }

    /**
     * Cookie lesen
     */
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);

        if (parts.length === 2) {
            try {
                const cookieValue = parts.pop().split(';').shift();
                return JSON.parse(decodeURIComponent(cookieValue));
            } catch (e) {
                return null;
            }
        }

        return null;
    }

    /**
     * Cookie schreiben
     * @param {boolean} persistent - false schreibt ein Sitzungs-Cookie ohne
     *   "expires" (gilt nur für die aktuelle Browser-Sitzung).
     */
    setCookie(name, value, persistent = true) {
        const cookieValue = encodeURIComponent(JSON.stringify(value));
        let cookieString = `${name}=${cookieValue}; path=/; SameSite=Lax`;

        if (persistent) {
            const expiryDays = this.config.cookies?.acceptDuration || 365;
            const date = new Date();
            date.setTime(date.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
            cookieString += `; expires=${date.toUTCString()}`;
        }

        document.cookie = cookieString;
    }

    /**
     * Beobachtet neue Inhalte (z.B. durch AJAX geladen oder nach Lazy-Load-Klick)
     */
    observeNewContent() {
        // Frühzeitiger Exit wenn zum Initialisierungszeitpunkt kein Blocking nötig ist.
        // shouldBlockService() prüft Consent dynamisch, daher stimmt dieser Check
        // auch nach späterem Consent-Widerruf; beim Accept ist ein laufender Observer
        // unschädlich, da innerhalb der Callback-Auswertung neu geprüft wird.
        const needsBlocking = this.shouldBlockYouTube() || this.shouldBlockVimeo() ||
            this.shouldBlockDailymotion() || this.shouldBlockSpotify() || this.shouldBlockGoogleMaps() || this.shouldBlockSoundCloud() ||
            this.shouldBlockOpenStreetMap();

        if (!needsBlocking) {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) { // Element node
                        // Consent-Status bei jedem Callback neu ermitteln, damit Änderungen
                        // durch acceptAll() / unlockContent() sofort wirken.
                        const shouldBlockYT = this.shouldBlockYouTube();
                        const shouldBlockVM = this.shouldBlockVimeo();
                        const shouldBlockDM = this.shouldBlockDailymotion();
                        const shouldBlockSP = this.shouldBlockSpotify();
                        const shouldBlockGM = this.shouldBlockGoogleMaps();
                        const shouldBlockSC = this.shouldBlockSoundCloud();
                        const shouldBlockOSM = this.shouldBlockOpenStreetMap();

                        // Prüfe ob das Element selbst ein Elementor-Widget ist
                        if (node.classList && node.classList.contains('elementor-widget-video')) {
                            const settings = node.getAttribute('data-settings');
                            if (settings) {
                                try {
                                    const settingsObj = JSON.parse(settings);
                                    const realSrc = node.getAttribute('data-pixelmate-video-src') || '';
                                    const effectiveText = realSrc || settings;

                                    if (shouldBlockYT && (effectiveText.includes('youtube') || effectiveText.includes('youtu.be'))) {
                                        pmLog('Detected new Elementor YouTube widget');
                                        this.blockElementorWidget(node, settingsObj, 'youtube');
                                    } else if (shouldBlockVM && effectiveText.includes('vimeo')) {
                                        pmLog('Detected new Elementor Vimeo widget');
                                        this.blockElementorWidget(node, settingsObj, 'vimeo');
                                    } else if (shouldBlockDM && (effectiveText.includes('dailymotion') || effectiveText.includes('dai.ly'))) {
                                        pmLog('Detected new Elementor Dailymotion widget');
                                        this.blockElementorWidget(node, settingsObj, 'dailymotion');
                                    } else if (shouldBlockSP && effectiveText.includes('spotify')) {
                                        pmLog('Detected new Elementor Spotify widget');
                                        this.blockElementorWidget(node, settingsObj, 'spotify');
                                    }
                                } catch (e) {
                                    // JSON parse error
                                }
                            }
                        }

                        // Prüfe ob das Element selbst ein iframe ist
                        if (node.tagName === 'IFRAME') {
                            if (shouldBlockYT && this.isYouTubeIframe(node)) {
                                this.handleIframe(node, 'youtube');
                            } else if (shouldBlockVM && this.isVimeoIframe(node)) {
                                this.handleIframe(node, 'vimeo');
                            } else if (shouldBlockDM && this.isDailymotionIframe(node)) {
                                this.handleIframe(node, 'dailymotion');
                            } else if (shouldBlockSP && this.isSpotifyIframe(node)) {
                                this.handleIframe(node, 'spotify');
                            } else if (shouldBlockGM && this.isGoogleMapsIframe(node)) {
                                this.handleIframe(node, 'google_maps');
                            } else if (shouldBlockSC && this.isSoundCloudIframe(node)) {
                                this.handleIframe(node, 'soundcloud');
                            }
                        }

                        // Prüfe ob das Element Elementor-Widgets enthält
                        const elementorWidgets = node.querySelectorAll?.('.elementor-widget-video[data-settings]');
                        elementorWidgets?.forEach(widget => {
                            const settings = widget.getAttribute('data-settings');
                            if (settings) {
                                try {
                                    const settingsObj = JSON.parse(settings);
                                    const realSrc = widget.getAttribute('data-pixelmate-video-src') || '';
                                    const effectiveText = realSrc || settings;

                                    if (shouldBlockYT && (effectiveText.includes('youtube') || effectiveText.includes('youtu.be'))) {
                                        pmLog('Detected Elementor YouTube widget in subtree');
                                        this.blockElementorWidget(widget, settingsObj, 'youtube');
                                    } else if (shouldBlockVM && effectiveText.includes('vimeo')) {
                                        pmLog('Detected Elementor Vimeo widget in subtree');
                                        this.blockElementorWidget(widget, settingsObj, 'vimeo');
                                    } else if (shouldBlockDM && (effectiveText.includes('dailymotion') || effectiveText.includes('dai.ly'))) {
                                        pmLog('Detected Elementor Dailymotion widget in subtree');
                                        this.blockElementorWidget(widget, settingsObj, 'dailymotion');
                                    } else if (shouldBlockSP && effectiveText.includes('spotify')) {
                                        pmLog('Detected Elementor Spotify widget in subtree');
                                        this.blockElementorWidget(widget, settingsObj, 'spotify');
                                    }
                                } catch (e) {
                                    // JSON parse error
                                }
                            }
                        });

                        // Prüfe ob das Element ein OpenStreetMap/Leaflet-Container ist
                        if (node.classList && node.classList.contains('leaflet-container') && !node.hasAttribute('data-pixelmate-blocked')) {
                            if (shouldBlockOSM && this.isOpenStreetMapContainer(node)) {
                                pmLog('Detected new OpenStreetMap container');
                                this.blockOpenStreetMapContainer(node);
                            }
                        }

                        // Prüfe ob das Element Leaflet-Container enthält
                        const leafletMaps = node.querySelectorAll?.('.leaflet-container:not([data-pixelmate-blocked])');
                        leafletMaps?.forEach(map => {
                            if (shouldBlockOSM && this.isOpenStreetMapContainer(map)) {
                                pmLog('Detected OpenStreetMap container in subtree');
                                this.blockOpenStreetMapContainer(map);
                            }
                        });

                        // Nachträglich geladene OSM-Tiles (oder der Attribution-Hinweis)
                        // innerhalb eines bereits vorhandenen, aber noch nicht als OSM
                        // erkannten Leaflet-Containers: Wird eine Karte leer ins DOM
                        // eingefügt und lädt Leaflet ihre Tiles erst asynchron nach,
                        // liefert isOpenStreetMapContainer() beim ersten Durchlauf
                        // "false" (noch keine Tiles/Attribution vorhanden) - ohne diesen
                        // zweiten Check würde der Container nie erneut geprüft.
                        if (shouldBlockOSM) {
                            const isOsmTileNode = node.tagName === 'IMG'
                                ? (node.src || '').includes('tile.openstreetmap.org')
                                : node.querySelector?.('img[src*="tile.openstreetmap.org"], .leaflet-control-attribution');
                            if (isOsmTileNode) {
                                const parentMap = node.closest?.('.leaflet-container:not([data-pixelmate-blocked])');
                                if (parentMap && this.isOpenStreetMapContainer(parentMap)) {
                                    pmLog('Detected late-loading OpenStreetMap tiles, blocking parent container');
                                    this.blockOpenStreetMapContainer(parentMap);
                                }
                            }
                        }

                        // Prüfe ob das Element iframes enthält
                        const iframes = node.querySelectorAll?.('iframe');
                        iframes?.forEach(iframe => {
                            if (shouldBlockYT && this.isYouTubeIframe(iframe)) {
                                this.handleIframe(iframe, 'youtube');
                            } else if (shouldBlockVM && this.isVimeoIframe(iframe)) {
                                this.handleIframe(iframe, 'vimeo');
                            } else if (shouldBlockDM && this.isDailymotionIframe(iframe)) {
                                this.handleIframe(iframe, 'dailymotion');
                            } else if (shouldBlockSP && this.isSpotifyIframe(iframe)) {
                                this.handleIframe(iframe, 'spotify');
                            } else if (shouldBlockGM && this.isGoogleMapsIframe(iframe)) {
                                this.handleIframe(iframe, 'google_maps');
                            } else if (shouldBlockSC && this.isSoundCloudIframe(iframe)) {
                                this.handleIframe(iframe, 'soundcloud');
                            }
                        });
                    }
                });
            });
        });

        // Observer SOFORT starten, auch vor DOMContentLoaded
        if (document.body) {
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        } else {
            // Falls body noch nicht existiert, warten
            const bodyObserver = new MutationObserver((mutations, obs) => {
                if (document.body) {
                    observer.observe(document.body, {
                        childList: true,
                        subtree: true
                    });
                    obs.disconnect();
                }
            });
            bodyObserver.observe(document.documentElement, {
                childList: true
            });
        }
    }

    /**
     * Gibt die Konfiguration für einen Service zurück (Titel, Anzeigename, Text).
     * Keine Logos/Icons mehr (siehe createConsentBanner) - daher enthält die
     * Konfiguration bewusst kein SVG-Icon.
     */
    getServiceConfig(service) {
        const configs = {
            youtube: {
                title: 'YouTube-Video',
                name: 'YouTube',
                defaultText: 'Um dieses YouTube-Video anzusehen, müssen Sie externe Inhalte zulassen.'
            },
            vimeo: {
                title: 'Vimeo-Video',
                name: 'Vimeo',
                defaultText: 'Um dieses Vimeo-Video anzusehen, müssen Sie externe Inhalte zulassen.'
            },
            dailymotion: {
                title: 'Dailymotion-Video',
                name: 'Dailymotion',
                defaultText: 'Um dieses Dailymotion-Video anzusehen, müssen Sie externe Inhalte zulassen.'
            },
            spotify: {
                title: 'Spotify-Inhalt',
                name: 'Spotify',
                defaultText: 'Um diese Spotify-Inhalte anzuzeigen, müssen Sie externe Inhalte zulassen.'
            },
            google_maps: {
                title: 'Google Maps',
                name: 'Google Maps',
                defaultText: 'Um diese Google Maps Karte anzuzeigen, müssen Sie externe Inhalte zulassen.'
            },
            soundcloud: {
                title: 'SoundCloud-Inhalt',
                name: 'SoundCloud',
                defaultText: 'Um diese SoundCloud-Inhalte anzuzeigen, müssen Sie externe Inhalte zulassen.'
            },
            open_street_map: {
                title: 'OpenStreetMap-Karte',
                name: 'OpenStreetMap',
                defaultText: 'Um diese OpenStreetMap-Karte anzuzeigen, müssen Sie externe Inhalte akzeptieren.'
            }
        };

        return configs[service] || configs.youtube;
    }

    /**
     * Prüft ob ein iframe ein SoundCloud-Widget ist
     */
    isSoundCloudIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('soundcloud.com') || src.includes('w.soundcloud.com');
    }

    /**
     * Prüft ob ein iframe ein YouTube-Video ist
     */
    isYouTubeIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('youtube.com') ||
               src.includes('youtube-nocookie.com') ||
               src.includes('youtu.be');
    }

    /**
     * Prüft ob ein iframe ein Vimeo-Video ist
     */
    isVimeoIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('vimeo.com') || src.includes('player.vimeo.com');
    }

    /**
     * Prüft ob ein iframe ein Dailymotion-Video ist
     */
    isDailymotionIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('dailymotion.com') || src.includes('dai.ly');
    }

    /**
     * Prüft ob ein iframe ein Spotify-Widget ist
     */
    isSpotifyIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('spotify.com') || src.includes('open.spotify.com');
    }

    /**
     * Prüft ob ein iframe eine Google Maps Karte ist
     */
    isGoogleMapsIframe(iframe) {
        const src = iframe.src || iframe.dataset.src || '';
        return src.includes('maps.google.com') ||
               src.includes('maps.googleapis.com') ||
               src.includes('google.com/maps');
    }

    /**
     * Konvertiert eine YouTube-URL zu youtube-nocookie.com (datenschutzfreundlich).
     * Wird nur angewendet wenn activeServices.youtube.useNoCookie aktiviert ist.
     * youtube-nocookie.com setzt keine Tracking-Cookies bevor der User das Video abspielt.
     */
    getYouTubeNoCookieUrl(url) {
        if (!url) return url;
        // Nur umschreiben wenn youtube.com (nicht bereits nocookie)
        if (url.includes('youtube-nocookie.com')) return url;
        return url
            .replace(/https?:\/\/(www\.)?youtube\.com\//g, 'https://www.youtube-nocookie.com/')
            .replace(/https?:\/\/youtu\.be\//g, 'https://www.youtube-nocookie.com/embed/');
    }

    /**
     * Gibt die finale URL für einen Service zurück.
     * Für YouTube: optional youtube-nocookie.com statt youtube.com
     */
    getActivationUrl(url, service) {
        if (service === 'youtube' && this.activeServices['youtube']?.useNoCookie) {
            return this.getYouTubeNoCookieUrl(url);
        }
        return url;
    }

    /**
     * Lädt bestehenden Consent und entsperrt Content falls bereits zugestimmt
     */
    loadConsent(consentData) {
        // Service-spezifische Consents laden
        if (consentData && consentData.services) {
            Object.keys(consentData.services).forEach(service => {
                if (consentData.services[service] && this.serviceConsent.hasOwnProperty(service)) {
                    this.serviceConsent[service] = true;
                    pmLog(`Service ${service} consent already granted`);
                }
            });
        }

        // Fallback: Alte kategorie-basierte Consents (für Kompatibilität)
        if (consentData && consentData.categories && consentData.categories.external) {
            // Wenn externe Kategorie aktiviert ist, alle Services aktivieren
            Object.keys(this.serviceConsent).forEach(service => {
                this.serviceConsent[service] = true;
            });
            pmLog('External category consent granted - all services enabled');
        }

        // Immer scannen und blockieren (nur Services ohne Consent werden blockiert)
        this.init();
    }
}

